import { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  TabPanel,
  TabPanels,
  Tabs,
  TabsRoot,
  type TabSpec,
} from "@norkron/web-tabs";
import { api } from "../api";
import type { AdminUser } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { AccessAdmin } from "../components/admin/AccessAdmin";
import { PermissionMatrix } from "../components/admin/PermissionMatrix";
import { Button, Switch } from "../compose/controls";
import { EmptyState, ErrorState, SkeletonRows } from "../compose/states";
import { useToast } from "../compose/Toast";
import { isSurfaceNotServed, readableError } from "../lib/errorCopy";
import { useAsync } from "../lib/useAsync";

/** The admin console's sections. `access` is the privz surface (Wave 5). */
type AdminTab = "users" | "permissions" | "access";

const ADMIN_TABS: TabSpec<AdminTab>[] = [
  { value: "users", label: "Users", testId: "admin-tab-users" },
  { value: "permissions", label: "Permissions", testId: "admin-tab-permissions" },
  {
    value: "access",
    label: "Access",
    testId: "admin-tab-access",
    helpText:
      "Groups, role bundles, grants, elevation and access reviews — resolved and enforced server-side.",
  },
];

/**
 * Admin `/w/:slug/admin` — the staff surface: modify users, reset passwords,
 * assign the INTERNAL/EXTERNAL team, the permission MATRIX (roles × capabilities
 * toggles, NOT dropdowns), and the privz ACCESS console. Client-gated on
 * `is_staff` — convenience only; the ops-plane `AdminOps` is authoritative.
 *
 * ── THE TAB STRIP IS `@norkron/web-tabs`, AND THAT IS A FIX ────────────────
 *
 * It used to be three hand-written `<button role="tab">`s inside a
 * `role="tablist"` — a tab widget missing every part of the tab contract: no
 * `aria-controls`, no `role="tabpanel"` anywhere, no arrow keys, and the selection
 * held in `useState` so a section could not be linked to or reloaded into. Roles
 * without the behaviour they promise are worse than no roles: a screen-reader user
 * is told to expect arrow-key navigation into an associated panel and gets
 * neither.
 *
 * `<Tabs>` + `<TabPanels>` supplies all of it, and the section moves into the URL
 * — so "the Access tab of this workspace's admin" becomes a link.
 *
 * ⚠️ The URL sync is elaborate's `useSearchParams`, NOT the lib's `urlParam` prop.
 * `defaultTabUrlSyncAdapter` calls `window.history.pushState` directly, which in a
 * react-router app is a SECOND writer to the same history stack that the router
 * never learns about — the `?tab=` would be dropped by the next router navigation
 * and Back would move through entries the router never recorded. Every other
 * URL-synced screen here (Tickets, Articles, Proposals) already drives
 * `useSearchParams`, so this keeps one history writer and one convention.
 *
 * `lazy` is deliberate: the Access panel boots a WASM resolver, and mounting that
 * for someone reading the user list would be a cost with no purchase.
 */
export function Admin() {
  const { slug = "" } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const raw = params.get("tab");
  const tab: AdminTab = ADMIN_TABS.some((t) => t.value === raw)
    ? (raw as AdminTab)
    : "users";
  const setTab = (next: AdminTab) => {
    const nextParams = new URLSearchParams(params);
    // `users` is the default; keeping it out of the URL leaves a plain
    // `/w/:slug/admin` link meaning what it always meant.
    if (next === "users") nextParams.delete("tab");
    else nextParams.set("tab", next);
    setParams(nextParams, { replace: true });
  };

  if (!user?.is_staff) {
    return (
      <div className="screen" data-testid="admin-forbidden">
        <EmptyState
          title="Staff only"
          body="The admin console is restricted to internal (Cerebr8) staff."
        />
      </div>
    );
  }

  return (
    <div className="screen admin" data-testid="admin-screen">
      {/* ⛔ `TabsRoot` is not decoration. `<Tabs>` emits `aria-controls` ONLY for a
          value it can see a registered panel for — deliberately, so a tab can never
          point at an id that is not there — and that registration flows through this
          root. Mounted as siblings without it, the strip and the panels each mint
          their own `useId()` prefix, the references diverge, and the widget is back
          to tab roles with no panel association: the exact defect the hand-rolled
          strip had. */}
      <TabsRoot idPrefix="admin">
        <header className="screen__head">
          <h1>Admin</h1>
          <Tabs
            tabs={ADMIN_TABS}
            value={tab}
            onChange={setTab}
            ariaLabel="Admin sections"
            testId="admin-tabs"
          />
        </header>

        <TabPanels value={tab} lazy="on-first-visit">
          <TabPanel value="users">
            <UsersTab />
          </TabPanel>
          <TabPanel value="permissions">
            <PermissionsTab slug={slug} />
          </TabPanel>
          <TabPanel value="access">
            {/* The account whose groups this console administers is the signed-in
                staff principal's own account; the scope is the workspace, which is
                elaborate's tenant unit. */}
            <AccessAdmin accountId={user.id} workspaceSlug={slug} />
          </TabPanel>
        </TabPanels>
      </TabsRoot>
    </div>
  );
}

/**
 * The one shape both server-backed admin tabs degrade into.
 *
 * A tab whose whole surface answers `404` is not broken — elaborate simply does
 * not serve that surface on the plane the client is on. That deserves a CALM
 * notice with the true next step, not a red alert and a Retry button that can
 * only fail again. Anything else is a real failure and keeps the retry.
 *
 * ⛔ No status code, no route, no binary name reaches the reader. The diagnosis
 * belongs in the console and in the handoff, not on a client's screen.
 */
function unservedOr(
  error: unknown,
  copy: {
    readonly unservedTitle: string;
    readonly unservedBody: string;
    readonly testId: string;
    readonly errorTitle: string;
    readonly onRetry: () => void;
  },
) {
  if (isSurfaceNotServed(error)) {
    return (
      <div data-testid={copy.testId}>
        <EmptyState title={copy.unservedTitle} body={copy.unservedBody} />
      </div>
    );
  }
  return (
    <ErrorState
      title={copy.errorTitle}
      body={readableError(error, "Please try again in a moment.")}
      action={<Button onClick={copy.onRetry}>Try again</Button>}
    />
  );
}

function UsersTab() {
  const toast = useToast();
  const users = useAsync(() => api.listUsers(), []);
  const [busy, setBusy] = useState<string | null>(null);

  if (users.loading) return <SkeletonRows rows={5} />;
  // ⛔ The 404 case is NOT an error to a reader. `/api/admin/*` is implemented
  // only in `elaborate-ops`, the Tailscale-scoped staff binary, and the public
  // plane has no such route — so the public app 404s. That is a deployment fact,
  // not a fault, and it says so in words a client can act on.
  if (users.error)
    return unservedOr(users.error, {
      unservedTitle: "Managing people isn't available here",
      unservedBody:
        "Adding people, resetting passwords and switching someone between the " +
        "internal team and a client is done from the internal Cerebr8 console. " +
        "Nobody's access has changed.",
      testId: "admin-users-unavailable",
      errorTitle: "We couldn't load the people in this workspace",
      onRetry: users.reload,
    });
  const rows = users.data ?? [];
  if (rows.length === 0)
    return (
      <EmptyState
        title="Nobody here yet"
        body="Once people are invited to this workspace they will appear here."
      />
    );

  const withBusy = async (id: string, fn: () => Promise<unknown>) => {
    setBusy(id);
    try {
      await fn();
      users.reload();
    } catch (e) {
      toast.show(readableError(e, "That didn't work. Please try again."));
    } finally {
      setBusy(null);
    }
  };

  const toggleStaff = (u: AdminUser) =>
    withBusy(u.id, () => api.setUserStaff(u.id, !u.is_staff));

  const toggleStatus = (u: AdminUser) =>
    withBusy(u.id, () =>
      api.setUserStatus(u.id, u.status === "active" ? "suspended" : "active"),
    );

  const resetPassword = (u: AdminUser) =>
    withBusy(u.id, async () => {
      const res = await api.resetUserPassword(u.id);
      if ("sso_managed" in res && res.sso_managed) {
        toast.show(`${u.name}: managed by SSO — no password to reset`);
      } else if ("reset_url" in res && res.reset_url) {
        await navigator.clipboard?.writeText(res.reset_url).catch(() => {});
        toast.show(`Reset link copied for ${u.name}`);
      }
    });

  return (
    <div className="admin-users" data-testid="admin-users">
      <table className="admin-users__table">
        <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">Email</th>
            <th scope="col">Auth</th>
            <th scope="col">Internal</th>
            <th scope="col">Active</th>
            <th scope="col">Password</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((u) => (
            <tr key={u.id} data-testid={`admin-user-${u.id}`}>
              <td>{u.name}</td>
              <td className="admin-users__email">{u.email}</td>
              <td>
                <span
                  className={`badge badge--${u.auth_method}`}
                  data-testid={`user-authmethod-${u.id}`}
                >
                  {u.auth_method === "sso"
                    ? "SSO"
                    : u.auth_method === "invited"
                      ? "Invited"
                      : "Password"}
                </span>
              </td>
              <td>
                <Switch
                  checked={u.is_staff}
                  disabled={busy === u.id}
                  label={`${u.name} internal team`}
                  data-testid={`user-internal-toggle-${u.id}`}
                  onChange={() => toggleStaff(u)}
                />
              </td>
              <td>
                <Switch
                  checked={u.status === "active"}
                  disabled={busy === u.id}
                  label={`${u.name} active`}
                  data-testid={`user-status-toggle-${u.id}`}
                  onChange={() => toggleStatus(u)}
                />
              </td>
              <td>
                <Button
                  variant="ghost"
                  disabled={busy === u.id || u.auth_method === "sso"}
                  data-testid={`user-reset-password-${u.id}`}
                  onClick={() => resetPassword(u)}
                >
                  Reset password
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PermissionsTab({ slug }: { slug: string }) {
  const toast = useToast();
  const { user } = useAuth();
  const matrix = useAsync(() => api.getPermissionMatrix(slug), [slug]);
  const [busyCell, setBusyCell] = useState<string | null>(null);

  if (matrix.loading) return <SkeletonRows rows={6} />;
  // "Could not load the matrix" is what a client actually read here. "Matrix" is
  // the INTERNAL name for the roles × capabilities grid; nothing in this product's
  // vocabulary uses it, so to a reader it named nothing at all. Same 404 story as
  // the Users tab — `/api/admin/*` lives only on the staff binary.
  if (matrix.error)
    return unservedOr(matrix.error, {
      unservedTitle: "Permissions aren't available here",
      unservedBody:
        "What each role is allowed to do is set from the internal Cerebr8 " +
        "console. Nobody's permissions have changed.",
      testId: "admin-permissions-unavailable",
      errorTitle: "We couldn't load permissions",
      onRetry: matrix.reload,
    });
  if (!matrix.data) return null;

  const toggle = async (role: string, capability: string, allowed: boolean) => {
    const key = `${role}:${capability}`;
    setBusyCell(key);
    try {
      await api.setPermission(slug, { role, capability, allowed });
      matrix.reload();
    } catch (e) {
      toast.show(readableError(e, "We couldn't save that change."));
      matrix.reload();
    } finally {
      setBusyCell(null);
    }
  };

  const resetRole = async (role: string) => {
    try {
      await api.resetRolePermissions(slug, role);
      toast.show(`${role} is back to its default permissions`);
      matrix.reload();
    } catch (e) {
      toast.show(readableError(e, "We couldn't reset that role."));
    }
  };

  return (
    <div className="admin-permissions" data-testid="admin-permissions">
      <p className="admin-permissions__hint">
        Toggle a capability for a role. Changes apply to this workspace and are
        enforced server-side. The owner row is locked.
      </p>
      <PermissionMatrix
        matrix={matrix.data}
        canManage={!!user?.is_staff}
        busyCell={busyCell}
        onToggle={toggle}
        onResetRole={resetRole}
      />
    </div>
  );
}
